import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocalization } from '../../contexts/LocalizationContext';
import advertisementService from '../../services/advertisementService';
import { getProductImageUrl } from '../../utils/formatters';
import Loading from '../../components/Common/Loading';
import Button from '../../components/Common/Button';
import Table from '../../components/Common/Table';
import {
  AdvertisementStatus, AdvertisementMediaType, AdvertisementPlatformFilter,
  TYPE_LABELS, STATUS_LABELS, STATUS_BADGE_CLASS, PLATFORM_FILTER_LABELS, PLATFORM_LABELS,
  labelFor, formatIraqDateTime,
} from './adConstants';

const PAGE_SIZE = 20;

const GUID_PATTERN = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
const generatedNameRegex = new RegExp(`^(advertisement\\s+)?${GUID_PATTERN}(\\s*\\(copy\\))?$`, 'i');

const isGeneratedAdName = (value) => generatedNameRegex.test((value || '').trim());

const getAdDisplayTitle = (row, isRtl) => (
  (isRtl ? row.titleAr : row.titleEn)
  || (isRtl ? row.titleEn : row.titleAr)
  || (!isGeneratedAdName(row.internalName) ? row.internalName : null)
  || (isRtl ? 'إعلان بدون عنوان' : 'Untitled advertisement')
);

const getAdMetaLabel = (row, isRtl) => {
  const internal = row.internalName?.trim();
  if (internal && !isGeneratedAdName(internal) && internal !== getAdDisplayTitle(row, isRtl)) return internal;
  return `${labelFor(TYPE_LABELS, row.type, isRtl)} · ${row.placementCount ?? 0} ${isRtl ? 'موضع' : 'placements'}`;
};

const ActionIcon = ({ name }) => {
  const common = { width: 17, height: 17, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': 'true' };
  if (name === 'view') {
    return <svg {...common}><path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6Z" /><circle cx="12" cy="12" r="3" /></svg>;
  }
  if (name === 'edit') {
    return <svg {...common}><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5Z" /></svg>;
  }
  if (name === 'activate') {
    return <svg {...common}><path d="M20 6 9 17l-5-5" /></svg>;
  }
  if (name === 'disable') {
    return <svg {...common}><circle cx="12" cy="12" r="9" /><path d="m5 5 14 14" /></svg>;
  }
  if (name === 'duplicate') {
    return <svg {...common}><rect x="8" y="8" width="11" height="11" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v1" /></svg>;
  }
  return <svg {...common}><path d="M3 6h18" /><path d="M8 6V4h8v2" /><path d="M19 6l-1 14H6L5 6" /><path d="M10 11v5" /><path d="M14 11v5" /></svg>;
};

const AdvertisementsIndex = () => {
  const { isRtl } = useLocalization();
  const navigate = useNavigate();

  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [platformFilter, setPlatformFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  // Debounce the free-text search.
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const loadSummary = useCallback(() => {
    advertisementService.getSummary().then(setSummary).catch(() => setSummary(null));
  }, []);

  const loadList = useCallback(() => {
    setIsLoading(true);
    advertisementService.getList({
      search: search || undefined,
      status: statusFilter !== '' ? statusFilter : undefined,
      platform: platformFilter !== '' ? platformFilter : undefined,
      type: typeFilter !== '' ? typeFilter : undefined,
      page,
      size: PAGE_SIZE,
    })
      .then((result) => {
        setItems(result.items || []);
        setTotal(result.total || 0);
      })
      .catch(() => { setItems([]); setTotal(0); })
      .finally(() => setIsLoading(false));
  }, [search, statusFilter, platformFilter, typeFilter, page]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadList(); }, [loadList]);

  const refresh = () => { loadSummary(); loadList(); };

  const handleActivate = async (id) => {
    setBusyId(id);
    try { await advertisementService.activate(id); refresh(); }
    catch (err) { alert(err.response?.data?.message || err.message || (isRtl ? 'حدث خطأ' : 'Something went wrong')); }
    finally { setBusyId(null); }
  };

  const handleDisable = async (id) => {
    setBusyId(id);
    try { await advertisementService.disable(id); refresh(); }
    catch (err) { alert(err.response?.data?.message || err.message || (isRtl ? 'حدث خطأ' : 'Something went wrong')); }
    finally { setBusyId(null); }
  };

  const handleDuplicate = async (id) => {
    setBusyId(id);
    try { await advertisementService.duplicate(id); refresh(); }
    catch (err) { alert(err.response?.data?.message || err.message || (isRtl ? 'حدث خطأ' : 'Something went wrong')); }
    finally { setBusyId(null); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(isRtl ? 'هل أنت متأكد من حذف هذا الإعلان؟' : 'Are you sure you want to delete this advertisement?')) return;
    setBusyId(id);
    try { await advertisementService.remove(id); refresh(); }
    catch (err) { alert(err.response?.data?.message || err.message || (isRtl ? 'حدث خطأ' : 'Something went wrong')); }
    finally { setBusyId(null); }
  };

  const columns = [
    {
      header: isRtl ? 'الإعلان' : 'Advertisement',
      accessor: 'internalName',
      width: '34%',
      render: (value, row) => {
        const thumb = row.mediaType === AdvertisementMediaType.Image ? getProductImageUrl(row.mediaUrl) : null;
        const title = getAdDisplayTitle(row, isRtl);
        return (
          <div className="promo-cell">
            <div className="promo-cell-thumb">
              {thumb ? (
                <>
                  <img
                    src={thumb}
                    alt=""
                    onError={(e) => { e.currentTarget.parentElement?.classList.add('promo-cell-thumb-failed'); }}
                  />
                  <span className="promo-cell-thumb-empty">{row.mediaType === AdvertisementMediaType.Video ? '▶' : '—'}</span>
                </>
              ) : (
                <span className="promo-cell-thumb-empty">{row.mediaType === AdvertisementMediaType.Video ? '▶' : '—'}</span>
              )}
            </div>
            <div className="promo-cell-info">
              <div className="promo-cell-name">{title}</div>
              <div className="promo-cell-sub">{getAdMetaLabel(row, isRtl)}</div>
            </div>
          </div>
        );
      },
    },
    {
      header: isRtl ? 'التوزيع' : 'Delivery',
      accessor: 'platforms',
      width: '18%',
      render: (value, row) => (
        <div className="promo-delivery-cell">
          <div className="promo-platform-badges">
            {(value || []).length === 0 ? '—' : value.map((p) => (
              <span key={p} className="badge badge-neutral">{labelFor(PLATFORM_LABELS, p, isRtl)}</span>
            ))}
          </div>
          <div className="promo-muted-line">
            {labelFor(TYPE_LABELS, row.type, isRtl)} · {row.placementCount ?? 0} {isRtl ? 'موضع' : 'placements'}
          </div>
        </div>
      ),
    },
    {
      header: isRtl ? 'الحالة' : 'Status',
      accessor: 'status',
      width: '10%',
      render: (value) => <span className={`badge ${STATUS_BADGE_CLASS[value]}`}>{labelFor(STATUS_LABELS, value, isRtl)}</span>,
    },
    {
      header: isRtl ? 'تاريخ البدء' : 'Start Date',
      accessor: 'startAtUtc',
      width: '12%',
      render: (v) => <span className="promo-date-cell">{v ? formatIraqDateTime(v, isRtl) : (isRtl ? 'فوري' : 'Immediate')}</span>,
    },
    {
      header: isRtl ? 'تاريخ الانتهاء' : 'End Date',
      accessor: 'expireAtUtc',
      width: '12%',
      render: (v) => <span className="promo-date-cell">{v ? formatIraqDateTime(v, isRtl) : (isRtl ? 'بدون' : 'None')}</span>,
    },
    {
      header: isRtl ? 'الإجراءات' : 'Actions',
      accessor: 'id',
      width: '12%',
      render: (value, row) => {
        const isBusy = busyId === value;
        const canActivate = row.status !== AdvertisementStatus.Active && row.status !== AdvertisementStatus.Scheduled;
        const canDisable = row.status === AdvertisementStatus.Active || row.status === AdvertisementStatus.Scheduled;
        const viewLabel = isRtl ? 'عرض' : 'View';
        const editLabel = isRtl ? 'تعديل' : 'Edit';
        const activateLabel = isRtl ? 'تفعيل' : 'Activate';
        const disableLabel = isRtl ? 'تعطيل' : 'Disable';
        const duplicateLabel = isRtl ? 'تكرار' : 'Duplicate';
        const deleteLabel = isRtl ? 'حذف' : 'Delete';
        return (
          <div className="promo-row-actions">
            <Button size="small" variant="secondary" className="promo-icon-btn" title={viewLabel} aria-label={viewLabel} onClick={() => navigate(`/advertisements/${value}`)}>
              <ActionIcon name="view" />
            </Button>
            <Button size="small" variant="secondary" className="promo-icon-btn" title={editLabel} aria-label={editLabel} onClick={() => navigate(`/advertisements/${value}/edit`)}>
              <ActionIcon name="edit" />
            </Button>
            {canActivate && (
              <Button size="small" variant="success" className="promo-icon-btn" title={activateLabel} aria-label={activateLabel} disabled={isBusy} onClick={() => handleActivate(value)}>
                <ActionIcon name="activate" />
              </Button>
            )}
            {canDisable && (
              <Button size="small" variant="secondary" className="promo-icon-btn" title={disableLabel} aria-label={disableLabel} disabled={isBusy} onClick={() => handleDisable(value)}>
                <ActionIcon name="disable" />
              </Button>
            )}
            <Button size="small" variant="secondary" className="promo-icon-btn" title={duplicateLabel} aria-label={duplicateLabel} disabled={isBusy} onClick={() => handleDuplicate(value)}>
              <ActionIcon name="duplicate" />
            </Button>
            <Button size="small" variant="danger" className="promo-icon-btn promo-icon-btn-danger" title={deleteLabel} aria-label={deleteLabel} disabled={isBusy} onClick={() => handleDelete(value)}>
              <ActionIcon name="delete" />
            </Button>
          </div>
        );
      },
    },
  ];

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="advertisements-content">
      {/* Summary cards */}
      <div className="stats-grid mb-6">
        <div className="stat-card">
          <div className="stat-card-content">
            <div className="stat-icon stat-icon-success">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
            </div>
            <div className="stat-details">
              <div className="stat-number">{summary?.active ?? '—'}</div>
              <div className="stat-label">{isRtl ? 'نشطة' : 'Active'}</div>
            </div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-card-content">
            <div className="stat-icon stat-icon-info">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
            </div>
            <div className="stat-details">
              <div className="stat-number">{summary?.scheduled ?? '—'}</div>
              <div className="stat-label">{isRtl ? 'مجدولة' : 'Scheduled'}</div>
            </div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-card-content">
            <div className="stat-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
            </div>
            <div className="stat-details">
              <div className="stat-number">{summary?.draft ?? '—'}</div>
              <div className="stat-label">{isRtl ? 'مسودة' : 'Draft'}</div>
            </div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-card-content">
            <div className="stat-icon stat-icon-warning">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"></circle><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"></line></svg>
            </div>
            <div className="stat-details">
              <div className="stat-number">{summary?.disabled ?? '—'}</div>
              <div className="stat-label">{isRtl ? 'معطلة' : 'Disabled'}</div>
            </div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-card-content">
            <div className="stat-icon stat-icon-danger">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>
            </div>
            <div className="stat-details">
              <div className="stat-number">{summary?.expired ?? '—'}</div>
              <div className="stat-label">{isRtl ? 'منتهية' : 'Expired'}</div>
            </div>
          </div>
        </div>
        </div>

      <div className="card promo-list-card">
        <div className="card-header promo-filters-header">
          <div className="promo-filter-controls">
            <div className="search-box">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
              <input
                type="text"
                placeholder={isRtl ? 'بحث بالاسم أو العنوان...' : 'Search by name or title...'}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>
            <select className="form-select promo-filter-select" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
              <option value="">{isRtl ? 'كل الحالات' : 'All Statuses'}</option>
              {Object.values(AdvertisementStatus).map((v) => (
                <option key={v} value={v}>{labelFor(STATUS_LABELS, v, isRtl)}</option>
              ))}
            </select>
            <select className="form-select promo-filter-select" value={platformFilter} onChange={(e) => { setPlatformFilter(e.target.value); setPage(1); }}>
              {Object.values(AdvertisementPlatformFilter).map((v) => (
                <option key={v} value={v === AdvertisementPlatformFilter.All ? '' : v}>{labelFor(PLATFORM_FILTER_LABELS, v, isRtl)}</option>
              ))}
            </select>
            <select className="form-select promo-filter-select" value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}>
              <option value="">{isRtl ? 'كل الأنواع' : 'All Types'}</option>
              {Object.values(TYPE_LABELS).map((_, idx) => idx).map((v) => (
                <option key={v} value={v}>{labelFor(TYPE_LABELS, v, isRtl)}</option>
              ))}
            </select>
          </div>
          <Button variant="primary" className="promo-create-btn" onClick={() => navigate('/advertisements/create')}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" /></svg>
            {isRtl ? 'إعلان جديد' : 'New Advertisement'}
          </Button>
        </div>
        <div className="card-body p-0">
          {isLoading ? (
            <Loading text={isRtl ? 'جاري التحميل...' : 'Loading advertisements...'} />
          ) : (
            <div className="promo-table-scroll">
              <Table columns={columns} data={items} emptyMessage={isRtl ? 'لا توجد إعلانات' : 'No advertisements found'} />
            </div>
          )}
        </div>
        {totalPages > 1 && (
          <div className="card-footer promo-pagination">
            <span className="pagination-info">
              {isRtl ? `الصفحة ${page} من ${totalPages} · ${total} إعلان` : `Page ${page} of ${totalPages} · ${total} advertisements`}
            </span>
            <div className="pagination-controls">
              <Button variant="secondary" size="small" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                {isRtl ? 'السابق' : 'Prev'}
              </Button>
              <Button variant="secondary" size="small" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
                {isRtl ? 'التالي' : 'Next'}
              </Button>
            </div>
          </div>
        )}
      </div>

      <style>{`
        .advertisements-content {
          background:
            linear-gradient(180deg, rgba(248, 250, 252, 0) 0%, rgba(241, 245, 249, 0.72) 100%);
          padding: var(--sp-4) var(--sp-5) !important;
          height: calc(100vh - var(--header-h));
          overflow: hidden;
          display: flex;
          flex-direction: column;
          min-height: 0;
        }
        .advertisements-content .stats-grid {
          display: grid;
          grid-template-columns: repeat(5, minmax(150px, 1fr));
          gap: var(--sp-3) !important;
          margin-bottom: var(--sp-4) !important;
        }
        .advertisements-content .stat-card {
          border-radius: var(--r-lg) !important;
          box-shadow: 0 10px 28px -22px rgba(15, 23, 42, 0.42) !important;
        }
        .advertisements-content .stat-card:hover { transform: translateY(-3px) !important; }
        .advertisements-content .stat-card-content {
          min-height: 72px;
          padding: var(--sp-3) var(--sp-4);
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: var(--sp-3);
        }
        .advertisements-content .stat-icon {
          width: 42px !important;
          height: 42px !important;
          border-radius: var(--r-md) !important;
        }
        .advertisements-content .stat-number { font-size: 1.45rem !important; letter-spacing: 0 !important; }
        .advertisements-content .stat-label { margin-top: 2px !important; }
        .advertisements-content .card-header { padding: var(--sp-3) var(--sp-4) !important; }
        .promo-list-card {
          flex: 1;
          min-height: 0;
          display: flex;
          flex-direction: column;
        }
        .promo-list-card .card-body {
          flex: 1;
          min-height: 0;
          overflow: hidden;
        }
        .promo-list-card .card-footer {
          flex-shrink: 0;
        }
        .promo-filters-header {
          display: flex !important;
          flex-wrap: wrap;
          gap: var(--sp-3);
          align-items: center;
          justify-content: space-between;
          background: #fff !important;
        }
        .promo-filter-controls {
          display: flex;
          flex-wrap: wrap;
          gap: var(--sp-3);
          align-items: center;
        }
        .promo-filter-select {
          width: 190px;
          max-width: 190px;
          height: 44px;
          min-height: 44px;
          border-radius: var(--r-md) !important;
          background-color: #fff;
          font-size: 12px;
          font-weight: 600;
        }
        .promo-filters-header .search-box {
          width: 240px;
          max-width: 240px !important;
          height: 44px;
          min-height: 44px;
          border-color: var(--border);
          background: var(--neutral-50);
          border-radius: var(--r-md);
        }
        .promo-filters-header .search-box input {
          font-size: 12px;
          font-weight: 600;
        }
        .promo-create-btn {
          height: 44px;
          min-height: 44px;
          padding-inline: var(--sp-5) !important;
          border-radius: var(--r-md) !important;
          font-size: 12px !important;
          flex-shrink: 0;
        }
        .promo-cell { display: flex; align-items: center; gap: var(--sp-3); min-width: 280px; max-width: 420px; }
        .promo-cell-thumb {
          width: 78px; height: 54px; flex-shrink: 0; border-radius: var(--r-md); overflow: hidden;
          background: linear-gradient(135deg, var(--primary-50), #fff); display: flex; align-items: center; justify-content: center;
          border: 1px solid rgba(203, 213, 225, 0.8);
          box-shadow: inset 0 0 0 1px rgba(255,255,255,0.7);
          position: relative;
        }
        .promo-cell-thumb img { width: 100%; height: 100%; object-fit: cover; position: relative; z-index: 1; }
        .promo-cell-thumb-failed img { display: none; }
        .promo-cell-thumb-failed .promo-cell-thumb-empty { display: inline-flex; }
        .promo-cell-thumb-empty { color: var(--text-muted); font-size: var(--fs-sm); }
        .promo-cell-thumb img + .promo-cell-thumb-empty { display: none; }
        .promo-cell-name {
          max-width: 260px;
          font-weight: 800;
          color: var(--text);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .promo-cell-sub {
          max-width: 260px;
          margin-top: 4px;
          font-size: var(--fs-xs);
          color: var(--text-muted);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .promo-delivery-cell { display: grid; gap: 7px; min-width: 150px; }
        .promo-muted-line { color: var(--text-muted); font-size: 12px; font-weight: 600; }
        .promo-platform-badges { display: flex; gap: 5px; flex-wrap: wrap; }
        .promo-date-cell {
          display: inline-block;
          min-width: 82px;
          color: var(--neutral-700);
          font-size: 12px;
          font-weight: 800;
          line-height: 1.45;
          white-space: normal;
        }
        .promo-row-actions {
          display: flex;
          align-items: center;
          justify-content: flex-start;
          gap: 6px;
          min-width: 178px;
        }
        [dir="rtl"] .promo-row-actions { justify-content: flex-end; }
        .promo-row-actions .promo-icon-btn {
          width: 34px;
          height: 34px;
          min-width: 34px;
          min-height: 34px;
          padding: 0 !important;
          border-radius: var(--r-sm) !important;
          color: var(--primary);
          box-shadow: none !important;
        }
        .promo-row-actions .promo-icon-btn:hover:not(:disabled) {
          transform: translateY(-1px) !important;
          background: var(--primary-50) !important;
          border-color: var(--primary-200) !important;
        }
        .promo-row-actions .promo-icon-btn-danger {
          color: #fff;
        }
        .promo-row-actions .promo-icon-btn-danger:hover:not(:disabled) {
          background: var(--danger-dark) !important;
          border-color: var(--danger-dark) !important;
        }
        .promo-table-scroll {
          height: 100%;
          overflow: auto;
        }
        .promo-table-scroll .table { min-width: 940px; }
        .promo-table-scroll .table th { z-index: 2; }
        .promo-table-scroll .table td { padding-top: 12px !important; padding-bottom: 12px !important; }
        .promo-table-scroll .table tbody tr:hover {
          background: linear-gradient(90deg, rgba(30, 58, 95, 0.035), rgba(201, 169, 98, 0.04)) !important;
        }
        .promo-pagination { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: var(--sp-3); }
        [dir="rtl"] .promo-cell-name,
        [dir="rtl"] .promo-cell-sub { text-align: right; }
        @media (max-width: 768px) {
          .advertisements-content { padding: var(--sp-3) !important; }
          .advertisements-content .stats-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          .promo-filters-header,
          .promo-filter-controls { display: grid !important; grid-template-columns: 1fr; width: 100%; }
          .promo-filter-select { max-width: 100%; width: 100%; }
          .promo-filters-header .search-box { width: 100%; max-width: 100% !important; }
          .promo-create-btn { width: 100%; }
          .promo-cell { min-width: 300px; }
          .promo-cell-name, .promo-cell-sub { max-width: 230px; }
        }
        @media (max-width: 1180px) {
          .advertisements-content .stats-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
        }
      `}</style>
    </div>
  );
};

export default AdvertisementsIndex;
